import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Inbox, Send, Loader2, PlayCircle, ArrowLeft } from "lucide-react";

/**
 * Two-tab inbox for reels shared IN-PLATFORM:
 *   - Received: reels other Hifireels users sent to me
 *   - Sent: reels I've shared to other Hifireels users
 */
export default function ReelInbox() {
  const [tab, setTab] = useState("received");
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.allSettled([
      api.get("/reels/inbox/received"),
      api.get("/reels/inbox/sent"),
    ]).then(([r1, r2]) => {
      if (r1.status === "fulfilled") setReceived(r1.value.data || []);
      if (r2.status === "fulfilled") setSent(r2.value.data || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const timeAgo = (iso) => {
    if (!iso) return "";
    const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <div className="px-4 py-6" data-testid="reel-inbox-page">
      <div className="flex items-center gap-2 mb-4">
        <Link to="/" className="p-2 rounded-full bg-white/8 hover:bg-white/12" data-testid="inbox-back"><ArrowLeft className="w-4 h-4"/></Link>
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Reel Inbox</h1>
      </div>
      <p className="text-white/60 text-sm mb-5">Reels shared between you and other Hifireels friends</p>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("received")} data-testid="inbox-tab-received"
          className={`flex-1 py-2.5 rounded-full text-sm font-bold flex items-center justify-center gap-2 ${tab === "received" ? "bg-gradient-to-r from-[#00E5FF] to-[#A855F7] text-black" : "bg-white/5 text-white/70"}`}>
          <Inbox className="w-4 h-4"/>Received {received.length > 0 && <span className="text-[10px] bg-black/15 px-1.5 rounded-full">{received.length}</span>}
        </button>
        <button onClick={() => setTab("sent")} data-testid="inbox-tab-sent"
          className={`flex-1 py-2.5 rounded-full text-sm font-bold flex items-center justify-center gap-2 ${tab === "sent" ? "bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black" : "bg-white/5 text-white/70"}`}>
          <Send className="w-4 h-4"/>Sent {sent.length > 0 && <span className="text-[10px] bg-black/15 px-1.5 rounded-full">{sent.length}</span>}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]"/></div>
      ) : tab === "received" ? (
        received.length === 0 ? (
          <EmptyState icon={<Inbox className="w-10 h-10"/>} title="No reels received yet" body="When a Hifireels friend sends you a reel, it will show up here."/>
        ) : (
          <div className="space-y-2" data-testid="inbox-received-list">
            {received.map(item => {
              const p = item.reel_preview || {};
              return (
                <Link key={item.id} to={`/?reel=${p.reel_id}`} data-testid={`inbox-recv-${item.id}`}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 active:scale-[0.99]">
                  <div className="relative w-14 h-20 rounded-xl overflow-hidden bg-black/50 flex-shrink-0">
                    {p.thumbnail_url ? <img src={p.thumbnail_url} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-2xl">📹</div>}
                    <PlayCircle className="absolute inset-0 m-auto w-6 h-6 text-white/80"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <img src={item.from?.avatar} alt="" className="w-5 h-5 rounded-full border border-white/10"/>
                      <span className="text-xs font-bold truncate">@{item.from?.username || "user"}</span>
                      <span className="text-[10px] text-white/40 ml-auto flex-shrink-0">{timeAgo(item.created_at)}</span>
                    </div>
                    <div className="text-sm text-white/85 mt-1 line-clamp-2">{item.content}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        )
      ) : (
        sent.length === 0 ? (
          <EmptyState icon={<Send className="w-10 h-10"/>} title="No reels sent yet" body="Tap the More menu on any reel → 'Send to friends' to share it privately in Hifireels."/>
        ) : (
          <div className="space-y-2" data-testid="inbox-sent-list">
            {sent.map(item => {
              const rl = item.reel || {};
              return (
                <Link key={item.id} to={`/?reel=${rl.id}`} data-testid={`inbox-sent-${item.id}`}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 active:scale-[0.99]">
                  <div className="relative w-14 h-20 rounded-xl overflow-hidden bg-black/50 flex-shrink-0">
                    {rl.thumbnail_url ? <img src={rl.thumbnail_url} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-2xl">📹</div>}
                    <PlayCircle className="absolute inset-0 m-auto w-6 h-6 text-white/80"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-white/80 truncate">{rl.title || "Reel"}</span>
                      <span className="text-[10px] text-white/40 ml-auto flex-shrink-0">{timeAgo(item.created_at)}</span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] text-white/55">to</span>
                      {(item.recipients || []).slice(0, 4).map(u => (
                        <span key={u.id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#00E5FF]/15 text-[#00E5FF] font-bold">@{u.username}</span>
                      ))}
                      {(item.recipients || []).length > 4 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-white/70">+{(item.recipients || []).length - 4}</span>
                      )}
                    </div>
                    {item.message && <div className="text-xs text-white/65 mt-1 line-clamp-1">💬 {item.message}</div>}
                  </div>
                </Link>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

function EmptyState({ icon, title, body }) {
  return (
    <div className="text-center py-12 flex flex-col items-center gap-3">
      <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center text-white/40">{icon}</div>
      <div className="font-outfit font-extrabold text-lg">{title}</div>
      <p className="text-sm text-white/55 max-w-xs">{body}</p>
    </div>
  );
}
